import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cygnus_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cygnus_token');
      localStorage.removeItem('cygnus_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
};

// Agent API
export const agentAPI = {
  list: () => api.get('/agents'),
  catalog: () => api.get('/agents/catalog'),
  get: (id) => api.get(`/agents/${id}`),
  create: (data) => api.post('/agents', data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  invoke: (id, data) => api.post(`/agents/${id}/invoke`, data),
  executions: (id) => api.get(`/agents/${id}/executions`),
  tools: (id) => api.get(`/agents/${id}/tools`),
  toggleTool: (id, toolName) => api.post(`/agents/${id}/tools/${toolName}/toggle`),
  taskTypes: (id) => api.get(`/agents/${id}/task-types`),
};

// Search API
export const searchAPI = {
  query: (q) => api.get('/search', { params: { q } }),
};

// Onboarding API
export const onboardingAPI = {
  complete: (data) => api.post('/onboarding/complete', data),
};

// Tool API
export const toolAPI = {
  list: (params) => api.get('/tools', { params }),
  categories: () => api.get('/tools/categories'),
  get: (name) => api.get(`/tools/${name}`),
  propose: (data) => api.post('/tools/propose', data),
  proposed: () => api.get('/tools/proposed'),
  approve: (id) => api.post(`/tools/${id}/approve`),
  reject: (id, data) => api.post(`/tools/${id}/reject`, data),
};

// Escalation API
export const escalationAPI = {
  list: (params) => api.get('/escalations', { params }),
  get: (id) => api.get(`/escalations/${id}`),
  resolve: (id, data) => api.post(`/escalations/${id}/resolve`, data),
  stats: () => api.get('/escalations/stats'),
};

// Activity API
export const activityAPI = {
  list: (params) => api.get('/activity', { params }),
};

// Dashboard API
export const dashboardAPI = {
  kpis: () => api.get('/dashboard/kpis'),
  metrics: (period) => api.get('/dashboard/metrics', { params: { period } }),
  advancedMetrics: () => api.get('/dashboard/metrics/advanced'),
};

// Portal API
export const portalAPI = {
  summary: () => api.get('/portal/summary'),
};

// Report API
export const reportAPI = {
  list: () => api.get('/reports'),
  get: (type) => api.get(`/reports/${type}`),
};

// Integration API
export const integrationAPI = {
  list: () => api.get('/integrations'),
  sync: (id) => api.post(`/integrations/${id}/sync`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Execution API
export const executionAPI = {
  trace: (id) => api.get(`/executions/${id}/trace`),
};

// Audit API
export const auditAPI = {
  list: (params) => api.get('/audit', { params }),
  detailed: (params) => api.get('/audit/detailed', { params }),
  export: (data) => api.post('/audit/export', data),
};

// Notification API
export const notificationAPI = {
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Orchestration API
export const orchestrationAPI = {
  list: () => api.get('/orchestrations'),
  get: (id) => api.get(`/orchestrations/${id}`),
  startClose: (data) => api.post('/orchestration/close-process', data),
  completeTask: (orchId, taskIndex) => api.post(`/orchestrations/${orchId}/tasks/${taskIndex}/complete`),
};

// Knowledge API
export const knowledgeAPI = {
  list: (agentId) => api.get(`/agents/${agentId}/knowledge`),
  add: (agentId, data) => api.post(`/agents/${agentId}/knowledge`, data),
  delete: (agentId, itemId) => api.delete(`/agents/${agentId}/knowledge/${itemId}`),
};

// Health API
export const healthAPI = {
  check: () => api.get('/health'),
};

// Tool Sandbox API
export const sandboxAPI = {
  test: (toolId) => api.post(`/tools/${toolId}/sandbox-test`),
};

// Autonomous Control API
export const autonomousAPI = {
  status: () => api.get('/autonomous/status'),
  toggleAll: (enabled) => api.post('/autonomous/toggle-all', { enabled }),
  toggleAgent: (agentId, enabled) => api.post(`/autonomous/agents/${agentId}/toggle`, { enabled }),
  runNow: (agentId) => api.post(`/autonomous/agents/${agentId}/run-now`),
  updateSchedule: (agentId, data) => api.put(`/autonomous/agents/${agentId}/schedule`, data),
};

// Agent Clone/Delete API
export const agentCrudAPI = {
  clone: (id) => api.post(`/agents/${id}/clone`),
  delete: (id) => api.delete(`/agents/${id}`),
};

// CFO Directives API
export const cfoAPI = {
  dashboard: () => api.get('/cfo/dashboard'),
  briefings: (limit) => api.get('/cfo/briefings', { params: { limit } }),
  briefing: (id) => api.get(`/cfo/briefings/${id}`),
  healthHistory: (days) => api.get('/cfo/health-score-history', { params: { days } }),
  directives: () => api.get('/cfo/directives'),
  sendDirective: (data) => api.post('/comms/human-directive', data),
};

// Comms Inbox API
export const commsInboxAPI = {
  threads: (params) => api.get('/comms/inbox', { params }),
  thread: (threadId) => api.get(`/comms/inbox/${threadId}`),
};

// Marketplace API
export const marketplaceAPI = {
  list: (params) => api.get('/marketplace', { params }),
  get: (id) => api.get(`/marketplace/${id}`),
};

// Comms Network API
export const commsNetworkAPI = {
  data: (period) => api.get('/comms/network', { params: { period } }),
};

// Org Reparent API
export const orgAPI = {
  reparent: (nodeId, newParentId) => api.post(`/org/nodes/${nodeId}/reparent`, { new_parent_agent_id: newParentId }),
};

// Simulation API
export const simulationAPI = {
  status: () => api.get('/simulation/status'),
  startPerfectStorm: () => api.post('/simulation/perfect-storm/start'),
  stop: () => api.post('/simulation/perfect-storm/stop'),
  healthScore: () => api.get('/simulation/health-score'),
  cashWaterfall: () => api.get('/simulation/cash-waterfall'),
};

// Gateway API
export const gatewayAPI = {
  health: () => api.get('/gateway/health'),
  channels: (params) => api.get('/gateway/channels', { params }),
  channel: (id) => api.get(`/gateway/channels/${id}`),
  createChannel: (data) => api.post('/gateway/channels', data),
  updateChannel: (id, data) => api.patch(`/gateway/channels/${id}`, data),
  deleteChannel: (id) => api.delete(`/gateway/channels/${id}`),
  testChannel: (id) => api.post(`/gateway/channels/${id}/test`),
  pauseChannel: (id) => api.post(`/gateway/channels/${id}/pause`),
  resumeChannel: (id) => api.post(`/gateway/channels/${id}/resume`),
  channelMessages: (id, limit) => api.get(`/gateway/channels/${id}/messages`, { params: { limit } }),
  channelStats: (id) => api.get(`/gateway/channels/${id}/stats`),
  agentChannels: (agentId) => api.get(`/gateway/agents/${agentId}/channels`),
  sendMessage: (data) => api.post('/gateway/send', data),
  sendInbound: (data) => api.post('/gateway/inbound', data),
  getMessage: (id) => api.get(`/gateway/messages/${id}`),
  templates: () => api.get('/gateway/templates'),
  createTemplate: (data) => api.post('/gateway/templates', data),
};
